import React, { useEffect, useState } from "react";
import api, { fileUrl, uploadImage } from "@/lib/api";
import { Modal, Button, Badge, Empty } from "@/components/ui-kit";
import { Camera, CheckCircle2, XCircle, Euro, Save } from "lucide-react";
import { toast } from "sonner";

export default function CampModal({ eventId, title, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cost, setCost] = useState("");
  const [deposit, setDeposit] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/events/${eventId}/camp`).then((r) => {
      setData(r.data); setCost(String(r.data.cost || 0)); setDeposit(String(r.data.deposit || 0));
    }).catch(() => toast.error("Chargement impossible")).finally(() => setLoading(false));
  };
  useEffect(load, [eventId]);

  const staff = data?.is_staff;

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put(`/events/${eventId}/camp`, { cost: parseFloat(cost) || 0, deposit: parseFloat(deposit) || 0 }); toast.success("Stage mis à jour"); load(); }
    catch { toast.error("Erreur"); } finally { setSavingSettings(false); }
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const photo = await uploadImage(file); await api.put(`/events/${eventId}/camp`, { photo }); toast.success("Photo mise à jour"); load(); }
    catch { toast.error("Échec de l'upload"); } finally { setUploading(false); }
  };
  const setParticipation = async (member_id, status) => {
    try { await api.post(`/events/${eventId}/camp/participation`, { member_id, status }); toast.success(status === "in" ? "Participation confirmée" : "Réponse enregistrée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Modal open onClose={onClose} title={`Stage — ${title || ""}`}>
      {loading ? <Empty text="Chargement..." /> : !data ? <Empty text="Indisponible." /> : (
        <div className="space-y-5" data-testid="camp-modal">
          {/* Photo */}
          <div className="rounded-xl overflow-hidden border border-border bg-secondary/30">
            {data.photo ? <img src={fileUrl(data.photo)} alt="stage" className="w-full max-h-52 object-cover" data-testid="camp-photo" />
              : <div className="h-32 flex items-center justify-center text-muted-foreground"><Camera size={28} /></div>}
            {staff && (
              <label className="flex items-center justify-center gap-2 py-2 text-xs font-bold cursor-pointer hover:text-primary border-t border-border">
                <Camera size={14} /> {uploading ? "Envoi..." : "Changer la photo"}
                <input data-testid="camp-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} />
              </label>
            )}
          </div>

          {/* Tarifs */}
          {staff ? (
            <div className="grid grid-cols-2 gap-3 items-end" data-testid="camp-settings">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Coût / enfant (€)</label>
                <input data-testid="camp-cost-input" type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)}
                  className="w-full rounded-lg bg-secondary border border-border p-2.5 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Acompte (€)</label>
                <input data-testid="camp-deposit-input" type="number" step="0.01" value={deposit} onChange={(e) => setDeposit(e.target.value)}
                  className="w-full rounded-lg bg-secondary border border-border p-2.5 text-sm outline-none focus:border-primary" />
              </div>
              <Button data-testid="camp-save-settings" className="col-span-2" onClick={saveSettings} disabled={savingSettings}><Save size={15} /> {savingSettings ? "..." : "Enregistrer les tarifs"}</Button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap text-sm" data-testid="camp-info">
              <Badge color="blue"><Euro size={12} /> Coût : {data.cost} €</Badge>
              <Badge color="yellow">Acompte : {data.deposit} €</Badge>
            </div>
          )}

          {/* Participants */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{staff ? "Inscriptions & paiements" : "Participation de mon enfant"}</p>
            {data.rows.length ? (
              <div className="space-y-2" data-testid="camp-rows">
                {data.rows.map((r) => (
                  <div key={r.member_id} data-testid={`camp-row-${r.member_id}`} className="rounded-lg border border-border bg-secondary/30 p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <p className="font-semibold text-sm">{r.name}</p>
                      <div className="flex gap-1.5">
                        <button data-testid={`camp-in-${r.member_id}`} onClick={() => setParticipation(r.member_id, "in")}
                          className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${r.status === "in" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                          <CheckCircle2 size={14} /> Je participe
                        </button>
                        <button data-testid={`camp-out-${r.member_id}`} onClick={() => setParticipation(r.member_id, "out")}
                          className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${r.status === "out" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                          <XCircle size={14} /> Je ne participe pas
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                      <Badge color="blue">Coût : {r.cost} €{r.custom_cost ? " (ajusté)" : ""}</Badge>
                      <Badge color={r.remaining <= 0 ? "green" : "yellow"}>Reçu : {r.amount_received} € · reste {r.remaining} €</Badge>
                    </div>
                    {staff && (
                      <p className="text-[11px] text-muted-foreground mt-2" data-testid={`camp-financial-note-${r.member_id}`}>
                        Coût perso & encaissements dans <b>Financier</b>.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : <Empty text="Aucun enfant concerné par ce stage." />}
          </div>
        </div>
      )}
    </Modal>
  );
}
